import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sessionsApi } from '../lib/api';

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: session, isLoading } = useQuery({
    queryKey: ['session', id],
    queryFn: () => sessionsApi.get(id!),
    enabled: !!id,
  });

  const generateBlueprintMutation = useMutation({
    mutationFn: () => sessionsApi.generateBlueprint(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session', id] });
      navigate(`/sessions/${id}/blueprint`);
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => sessionsApi.resume(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session', id] });
    },
  });

  if (isLoading) {
    return <div className="text-center py-8 text-theme-secondary">読み込み中...</div>;
  }

  if (!session) {
    return <div className="text-center py-8 text-theme-secondary">セッションが見つかりません</div>;
  }

  const categoryLabels: Record<string, string> = {
    why: '目的・背景',
    who: 'ユーザー層',
    what: '機能・データ',
    flow: '業務フロー',
    vibe: 'デザイン・UI',
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <Link to="/sessions" className="text-sm text-theme-secondary hover:text-theme-primary mb-2 inline-block">
            ← セッション一覧
          </Link>
          <h1 className="text-2xl font-bold text-theme-primary">
            {session.initialIdea || 'セッション詳細'}
          </h1>
        </div>
        <div className="flex gap-2">
          {session.status === 'paused' && (
            <button
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              再開する
            </button>
          )}
          {session.status === 'completed' && (
            <button
              onClick={() => generateBlueprintMutation.mutate()}
              disabled={generateBlueprintMutation.isPending}
              className="px-4 py-2 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity"
            >
              設計書を生成
            </button>
          )}
          {session.status === 'completed' && (
            <Link
              to={`/sessions/${id}/blueprint`}
              className="px-4 py-2 border border-accent text-accent rounded-lg hover:bg-accent hover:text-navy-900 transition-colors"
            >
              設計書を見る
            </Link>
          )}
        </div>
      </div>

      {/* ステータスと進捗 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
          <p className="text-theme-secondary text-sm mb-1">ステータス</p>
          <p className="text-xl font-bold text-theme-primary">
            {session.status === 'completed' ? '完了' :
             session.status === 'in_progress' ? '進行中' :
             session.status === 'paused' ? '一時停止' : 'キャンセル'}
          </p>
        </div>
        <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
          <p className="text-theme-secondary text-sm mb-1">進捗</p>
          <p className="text-xl font-bold text-theme-primary">
            {session.answeredQuestions} / {session.totalQuestions}
          </p>
        </div>
        <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
          <p className="text-theme-secondary text-sm mb-1">ゲスト情報</p>
          <p className="text-theme-primary">
            {session.guestName || '(未入力)'}
          </p>
          {session.guestEmail && (
            <p className="text-sm text-theme-secondary">{session.guestEmail}</p>
          )}
        </div>
      </div>

      {/* 質問一覧 */}
      <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
        <h2 className="text-lg font-bold text-theme-primary mb-4">質問一覧</h2>
        <div className="space-y-3">
          {session.questions.map(q => (
            <div
              key={q.id}
              className={`p-4 rounded-lg ${q.isAnswered ? 'bg-theme-hover' : 'bg-theme-main'} transition-colors`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-1 rounded text-xs ${
                  q.isAnswered ? 'bg-green-500 bg-opacity-20 text-green-500' : 'bg-gray-500 bg-opacity-20 text-gray-500'
                }`}>
                  {q.isAnswered ? '回答済み' : '未回答'}
                </span>
                <span className="text-xs text-theme-secondary">
                  {categoryLabels[q.category] || q.category}
                </span>
              </div>
              <p className="text-theme-primary">Q{q.questionNumber}: {q.questionText}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 会話ログ */}
      <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
        <h2 className="text-lg font-bold text-theme-primary mb-4">会話ログ</h2>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {session.conversationLogs.map(log => (
            <div
              key={log.id}
              className={`p-4 rounded-lg ${
                log.role === 'jarvis' ? 'bg-theme-hover' : 'bg-accent bg-opacity-10'
              } transition-colors`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-accent">
                  {log.role === 'jarvis' ? '🤖 Jarvis' : '👤 ユーザー'}
                </span>
                <span className="text-xs text-theme-secondary">
                  {new Date(log.createdAt).toLocaleString('ja-JP')}
                </span>
              </div>
              <p className="text-theme-primary whitespace-pre-wrap">{log.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
